import axios, { AxiosHeaders } from 'axios';

import { API_URL } from 'src/constants/urls';
import { LEADSMANIA_PRODUCT_CODE, LEADSMANIA_PRODUCT_HEADER } from 'src/constants/product';
import SessionService from 'src/services/SessionService';
import { history } from 'src/utils/history';
import useSessionStore from 'src/stores/session';

import { enqueueSnackbarGlobal } from 'src/utils/snackbar';

import { ROUTES_URL } from 'src/constants/routes';

import ForbiddenError from './ForbiddenError';

import type { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';

const configuredApiTimeoutMs = Number(process.env.REACT_APP_API_TIMEOUT_MS);
const apiTimeoutMs =
    configuredApiTimeoutMs > 0 ? configuredApiTimeoutMs : process.env.NODE_ENV === 'development' ? 15000 : 0;

const defaultConfig: AxiosRequestConfig = {
    baseURL: API_URL,
    timeout: apiTimeoutMs,
    withCredentials: true,
};

const defaultHeaders: AxiosRequestHeaders = new AxiosHeaders();
defaultHeaders[LEADSMANIA_PRODUCT_HEADER] = LEADSMANIA_PRODUCT_CODE;

export function setDefaultHeaders(key: string, value: string) {
    defaultHeaders[key] = value;
}

export function removeHeaderFromDefaults(key: string | string[]) {
    defaultHeaders.delete(key);
}

export function createAxiosAgent(config: AxiosRequestConfig = {}) {
    const instance = axios.create({ ...defaultConfig, ...config });
    instance.interceptors.request.use(function (requestConfig) {
        requestConfig.headers = new AxiosHeaders({
            ...defaultHeaders,
            ...requestConfig.headers,
        });

        return requestConfig;
    });
    return instance;
}

const globalAgent = createAxiosAgent();

globalAgent.interceptors.request.use(function (config) {
    config.headers = new AxiosHeaders({
        ...defaultHeaders,
        ...config.headers,
    });

    // Логирование всех запросов для отладки
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    const logData: any = {
        url: config.url,
        baseURL: config.baseURL,
        fullURL: fullUrl,
        method: config.method?.toUpperCase(),
    };

    // Для запросов к update-onboarding-step логируем также данные
    if (config.url?.includes('update-onboarding-step')) {
        try {
            const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
            logData.data = data;
            logData.step = data?.step;
            console.log('🔐 [Agent] Request to update-onboarding-step:', {
                url: config.url,
                fullURL: fullUrl,
                method: config.method?.toUpperCase(),
                data: data,
                step: data?.step,
                dataType: typeof config.data,
            });
        } catch (e) {
            console.error('❌ [Agent] Ошибка парсинга data:', e, config.data);
        }
    } else {
        console.log('🔐 [Agent] Request:', logData);
        // Логируем body для create-payment запросов
        if (config.url?.includes('create-payment')) {
            try {
                const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
                console.log('🔐 [Agent] create-payment body:', data);
                console.log('🔐 [Agent] create-payment promo_code:', data?.promo_code);
            } catch (e) {
                console.error('❌ [Agent] Ошибка парсинга data:', e, config.data);
            }
        }
    }

    return config;
});

globalAgent.interceptors.response.use(
    (res) => {
        return res;
    },
    async (err) => {
        const originalConfig = err.config;
        if (!err.response) {
            return Promise.reject(err);
        }

        if (err.response.status === 401 && originalConfig && !originalConfig._retry) {
            originalConfig._retry = true;
            console.log('🔄 [Agent] Получена ошибка 401, обновляем токен...');
            try {
                await SessionService.updateToken();
                await useSessionStore.getState().syncLeadsmaniaProfile();
                console.log('✅ [Agent] Токен обновлен, повторяем запрос');
                originalConfig.headers['Authorization'] = `Bearer ${SessionService.accessToken}`;
                return globalAgent(originalConfig);
            } catch (error) {
                console.log('❌ [Agent] Ошибка обновления токена:', error);
                useSessionStore.getState().logout();
                return Promise.reject(error);
            }
        }

        if (err.response?.status === 404) {
            const isApiRequest = originalConfig?.baseURL === API_URL || typeof originalConfig?.url === 'string';
            // Не редиректим на 404 для API-запросов, чтобы не срывать UX экрана
            if (!isApiRequest) {
                history.push(ROUTES_URL.NOT_FOUND);
                throw new ForbiddenError('Доступ к данной странице запрещен');
            }
        }

        if (err.response?.status === 403) {
            const serverMessage = err.response?.data?.message;
            const message = typeof serverMessage === 'string' ? serverMessage : 'Запрашиваемая страница недоступна!';
            enqueueSnackbarGlobal(message, { variant: 'error' });
        }

        return Promise.reject(err);
    }
);

export default globalAgent;
